import type { FeedNotification } from '@/features/notifications/notification-feed';
import type { SidebarThread } from '@/lib/sidebar-threads';

/** What an app asks the user (`requests.create`): answered at the core, not to a turn. */
const APP_REQUEST_TYPE = 'crowdex/request.waiting';

/** One answer of a question, given without opening the conversation. */
type WaitingChoice = { value: string; label: string };

/** What a conversation waits on the user for: an approval or a question, from its event. */
export type Waiting = {
  notification: FeedNotification;
  kind: 'approval' | 'question';
  /**
   * Enough to answer an approval without opening the conversation: an agent turn's, or an app's
   * request (`turnId` null), answered at the core.
   */
  approval: { turnId: string | null; requestId: string } | null;
  /** What the agent asks, without the notification's prefix. */
  text: string;
  /** An approval's command, when its event carries it. */
  detail: string | null;
  /** A question's few answers and the field they fill: answered from its card. */
  question: {
    turnId: string | null;
    requestId: string;
    /** The form field the answer fills; `null`: the answer is a message to the turn. */
    field: string | null;
    choices: WaitingChoice[];
  } | null;
};

export type ActivityEntry = {
  thread: SidebarThread;
  waiting: Waiting | null;
  running: boolean;
};

/**
 * The conversations waiting on the user, from their unread events of a type that waits
 * (`waits`: an agent's approval or question, an automation's request): the latest of each
 * conversation (a reply, a failure or its group moving on since marked it read).
 */
export function waitingByThread(
  notifications: readonly FeedNotification[],
  waitingTypes: ReadonlySet<string>,
): ReadonlyMap<string, Waiting> {
  const waiting = new Map<string, Waiting>();
  for (const notification of notifications) {
    if (!waitingTypes.has(notification.type) || notification.readAt !== null) continue;
    if (notification.threadId === null || waiting.has(notification.threadId)) continue;
    const { kind, turn, request, detail, field, answer } = notification.fields;
    // An agent's is answered to its turn; an app's request to the core.
    const turnId =
      typeof turn === 'string' ? turn : notification.type === APP_REQUEST_TYPE ? null : undefined;
    const answerable = turnId !== undefined && typeof request === 'string';
    const choices = parseChoices(notification.fields['choices']);
    waiting.set(notification.threadId, {
      notification,
      kind: kind === 'question' ? 'question' : 'approval',
      approval:
        kind === 'approval' && answerable
          ? { turnId: turnId ?? null, requestId: request as string }
          : null,
      text: waitingText(notification.body) || notification.title,
      detail: typeof detail === 'string' && detail !== '' ? detail : null,
      question:
        kind === 'question' &&
        answerable &&
        choices.length > 0 &&
        (answer === 'message' || typeof field === 'string')
          ? {
              turnId: turnId ?? null,
              requestId: request as string,
              field: answer === 'message' ? null : (field as string),
              choices,
            }
          : null,
    });
  }
  return waiting;
}

/** The answers a question's event lists (JSON), the malformed ones left out. */
function parseChoices(value: unknown): WaitingChoice[] {
  if (typeof value !== 'string') return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.flatMap((choice) =>
          typeof choice === 'object' &&
          choice !== null &&
          typeof (choice as WaitingChoice).value === 'string' &&
          typeof (choice as WaitingChoice).label === 'string'
            ? [{ value: (choice as WaitingChoice).value, label: (choice as WaitingChoice).label }]
            : [],
        )
      : [];
  } catch {
    return [];
  }
}

/** The agent's words, without the "The agent asks:" a notification starts with. */
function waitingText(body: string | null): string {
  return (body ?? '').replace(/^(The agent asks|Waiting for your approval):\s*/, '').trim();
}

/**
 * What waits on the user, newest first: the conversations with an unread event that waits,
 * and those their server marks as needing an action.
 */
export function needsYouEntries(input: {
  threads: readonly SidebarThread[];
  waiting: ReadonlyMap<string, Waiting>;
}): ActivityEntry[] {
  return [...input.threads]
    .sort((left, right) => right.updatedAt - left.updatedAt)
    .flatMap((thread) => {
      const waiting = input.waiting.get(thread.id) ?? null;
      if (waiting === null && thread.actionRequired !== true) return [];
      return [
        {
          thread,
          waiting,
          running: thread.loading === true,
        },
      ];
    });
}
